import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
    ArrowLeft, Save, RotateCcw, Copy, Plus, Trash2, 
    Car, Fuel, Shield, Wrench, Battery, CircleDot,
    FileCheck, Gauge, Settings, ReceiptText, Loader2,
    TrendingUp, TrendingDown, Minus, Calculator as CalcIcon,
    FileText, Printer, CarFront
} from 'lucide-react';
import { toast } from 'sonner';
import VehiclePDF from '../components/VehiclePDF';
import VoiceInput from '../components/VoiceInput';
import { VehicleDocuments } from '../components/VehicleDocuments';
import { VehicleCommissionBlock } from '../components/VehicleCommissionBlock';
import { API_URL } from '../lib/api';
import { DatePickerField } from '../components/DatePickerField';

// Default fees
const DEFAULT_FEES = [
    { id: 'fee-1', nom: 'Point de contrôle', montant_ttc: 0, tva_recuperable: true, icon: 'check' },
    { id: 'fee-2', nom: 'Contrôle technique', montant_ttc: 0, tva_recuperable: true, icon: 'file' },
    { id: 'fee-3', nom: 'Plein de carburant', montant_ttc: 0, tva_recuperable: true, icon: 'fuel' },
    { id: 'fee-4', nom: 'Garantie', montant_ttc: 0, tva_recuperable: false, icon: 'shield' },
    { id: 'fee-5', nom: 'Révision mécanique', montant_ttc: 0, tva_recuperable: true, icon: 'wrench' },
    { id: 'fee-6', nom: 'Intervention mécanique', montant_ttc: 0, tva_recuperable: true, icon: 'settings' },
    { id: 'fee-7', nom: 'Batterie', montant_ttc: 0, tva_recuperable: true, icon: 'battery' },
    { id: 'fee-8', nom: 'Remplacement pneus', montant_ttc: 0, tva_recuperable: true, icon: 'circle' },
    { id: 'fee-9', nom: 'Freins', montant_ttc: 0, tva_recuperable: true, icon: 'gauge' },
    { id: 'fee-10', nom: 'Vidange de boîte', montant_ttc: 0, tva_recuperable: true, icon: 'settings' },
    { id: 'fee-11', nom: 'Autres frais', montant_ttc: 0, tva_recuperable: false, icon: 'receipt' },
];

const ICON_MAP = {
    check: FileCheck,
    file: FileCheck,
    fuel: Fuel,
    shield: Shield,
    wrench: Wrench,
    settings: Settings,
    battery: Battery,
    circle: CircleDot,
    gauge: Gauge,
    receipt: ReceiptText,
};

function getIcon(iconName) {
    const IconComponent = ICON_MAP[iconName] || ReceiptText;
    return <IconComponent className="w-4 h-4" />;
}

// Counter for generating unique IDs for custom fees
let feeIdCounter = 100;

function hl(highlights, field) {
    return highlights.has(field) ? 'ring-2 ring-emerald-400 bg-emerald-50 transition-all duration-500' : '';
}

export default function Calculator() {
    const { id } = useParams();
    const [commissionKey, setCommissionKey] = useState(0);
    const navigate = useNavigate();
    const { user } = useAuth();
    const isMobile = user?.role === 'user';
    const isAdmin = ['super_admin', 'admin', 'admin_agence'].includes(user?.role);
    const [loading, setLoading] = useState(!!id);
    const [saving, setSaving] = useState(false);
    const [showPDF, setShowPDF] = useState(false);
    const [voiceHighlights, setVoiceHighlights] = useState(new Set());
    const pdfRef = useRef(null);

    // Form state
    const [nom, setNom] = useState('');
    const [modele, setModele] = useState('');
    const [immatriculation, setImmatriculation] = useState('');
    const [prixAchat, setPrixAchat] = useState(0);
    const [prixVente, setPrixVente] = useState(0);
    const [fraisCirculation, setFraisCirculation] = useState(0);
    const [remiseClient, setRemiseClient] = useState(0);
    const [dateVente, setDateVente] = useState('');
    const [statut, setStatut] = useState('en_stock');
    const [commercialId, setCommercialId] = useState('');
    const [commerciaux, setCommerciaux] = useState([]);
    const [frais, setFrais] = useState(DEFAULT_FEES.map(f => ({ ...f })));
    const [skipMargeMin, setSkipMargeMin] = useState(false);
    const [regimeTva, setRegimeTva] = useState('');
    const [dupeCheck, setDupeCheck] = useState(null); // {results: [...], norm: ''}
    const [dupeForce, setDupeForce] = useState(false);
    const dupeTimer = useRef(null);

    // Live immatriculation check (debounce 500ms)
    const checkImmat = useCallback((val) => {
        if (dupeTimer.current) clearTimeout(dupeTimer.current);
        setDupeCheck(null);
        setDupeForce(false);
        const clean = val.replace(/[\s\-]/g, '').toUpperCase();
        if (clean.length < 4 || id) return; // Skip check when editing existing vehicle
        dupeTimer.current = setTimeout(() => {
            axios.get(`${API_URL}/api/vehicles/check-immat?immat=${encodeURIComponent(val)}`, { withCredentials: true })
                .then(r => { if (r.data.results?.length > 0) setDupeCheck(r.data); })
                .catch(() => {});
        }, 500);
    }, [id]);

    // Fetch commercial list
    useEffect(() => {
        axios.get(`${API_URL}/api/commerciaux`, { withCredentials: true })
            .then(res => setCommerciaux(res.data))
            .catch(() => {});
    }, []);

    // Fetch vehicle data
    const fetchVehicle = useCallback(async () => {
        if (!id) return;
        try {
            const { data } = await axios.get(`${API_URL}/api/vehicles/${id}`, {
                withCredentials: true
            });
            setNom(data.nom);
            setModele(data.modele || '');
            setImmatriculation(data.immatriculation || '');
            setPrixAchat(data.prix_achat);
            setPrixVente(data.prix_vente);
            setFraisCirculation(data.frais_circulation);
            setRemiseClient(data.remise_client);
            setDateVente(data.date_vente || '');
            setStatut(data.statut || 'en_stock');
            setCommercialId(data.commercial_id || '');
            setSkipMargeMin(data.skip_marge_min_agence || false);
            setRegimeTva(data.regime_tva || '');
            if (data.frais && data.frais.length > 0) {
                setFrais(data.frais.map((f, i) => ({
                    ...f,
                    id: f.id || `loaded-fee-${i}`,
                    icon: DEFAULT_FEES[i]?.icon || 'receipt'
                })));
            }
        } catch (error) {
            console.error('Error fetching vehicle:', error);
            const msg = error.response?.status === 403 ? 'Accès refusé à ce dossier' : 'Véhicule non trouvé';
            toast.error(msg);
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    // Load existing vehicle
    useEffect(() => {
        if (id) {
            fetchVehicle();
        }
    }, [id, fetchVehicle]);

    // Calculations
    const calculations = useMemo(() => {
        // 1. Vente réelle TTC
        const venteReelleTTC = prixVente + fraisCirculation - remiseClient;
        
        // 2. Marge brute TTC
        const margeBruteTTC = venteReelleTTC - prixAchat;
        
        // 3. TVA sur marge
        const tvaSurMarge = margeBruteTTC > 0 ? margeBruteTTC * 20 / 120 : 0;
        
        // 4. Marge brute HT avant frais
        const margeBruteHTAvantFrais = margeBruteTTC - tvaSurMarge;
        
        // 5. Calculate fees
        let totalFraisTTC = 0;
        let totalFraisHT = 0;
        let totalTVADeductible = 0;
        
        const fraisDetails = frais.map(fee => {
            const ttc = fee.montant_ttc || 0;
            totalFraisTTC += ttc;
            
            let ht, tva;
            if (fee.tva_recuperable) {
                ht = ttc / 1.20;
                tva = ttc - ht;
                totalTVADeductible += tva;
            } else {
                ht = ttc;
                tva = 0;
            }
            totalFraisHT += ht;
            
            return { ...fee, ht, tva };
        });
        
        // 8. Marge nette HT réelle
        const margeNetteHT = margeBruteHTAvantFrais - totalFraisHT;
        
        // 9. TVA nette dossier
        const tvaNetteDossier = tvaSurMarge - totalTVADeductible;
        
        return {
            venteReelleTTC,
            margeBruteTTC,
            tvaSurMarge,
            margeBruteHTAvantFrais,
            totalFraisTTC,
            totalFraisHT,
            totalTVADeductible,
            margeNetteHT,
            tvaNetteDossier,
            fraisDetails
        };
    }, [prixAchat, prixVente, fraisCirculation, remiseClient, frais]);

    // Get margin status - icons are stable imports, no need in deps
    const getMarginStatus = useCallback((margin) => {
        if (margin >= 1000) return { color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-500/20', label: 'Excellente', icon: TrendingUp };
        if (margin >= 500) return { color: 'text-amber-600', bg: 'bg-amber-500/10 border-amber-500/20', label: 'Correcte', icon: Minus };
        return { color: 'text-red-600', bg: 'bg-red-50 border-red-500/20', label: 'Faible', icon: TrendingDown };
    }, []);

    const marginStatus = getMarginStatus(calculations.margeNetteHT);
    const MarginIcon = marginStatus.icon;

    // Update fee
    const updateFee = useCallback((index, field, value) => {
        setFrais(prevFrais => {
            const newFrais = [...prevFrais];
            newFrais[index] = { ...newFrais[index], [field]: value };
            return newFrais;
        });
    }, []);

    // Add custom fee
    const addCustomFee = useCallback(() => {
        feeIdCounter++;
        setFrais(prevFrais => [
            ...prevFrais, 
            { id: `custom-fee-${feeIdCounter}`, nom: 'Nouveau frais', montant_ttc: 0, tva_recuperable: false, icon: 'receipt' }
        ]);
    }, []);

    // Remove fee
    const removeFee = useCallback((index) => {
        setFrais(prevFrais => prevFrais.filter((_, i) => i !== index));
    }, []);

    // Reset form
    const resetForm = useCallback(() => {
        setNom('');
        setModele('');
        setImmatriculation('');
        setPrixAchat(0);
        setPrixVente(0);
        setFraisCirculation(0);
        setRemiseClient(0);
        setDateVente('');
        setStatut('en_stock');
        setCommercialId('');
        setSkipMargeMin(false);
        setFrais(DEFAULT_FEES.map(f => ({ ...f })));
        toast.success('Formulaire réinitialisé');
    }, []);

    // Voice input handler
    const handleVoiceResult = useCallback((data, detectedFields) => {
        const highlights = new Set();
        if (data.nom) { setNom(data.nom); highlights.add('nom'); }
        if (data.modele) { setModele(data.modele); highlights.add('modele'); }
        if (data.immatriculation) { setImmatriculation(data.immatriculation.toUpperCase()); highlights.add('immatriculation'); }
        if (data.prix_achat != null) { setPrixAchat(data.prix_achat); highlights.add('prix_achat'); }
        if (data.prix_vente != null) { setPrixVente(data.prix_vente); highlights.add('prix_vente'); }
        if (data.frais_circulation != null) { setFraisCirculation(data.frais_circulation); highlights.add('frais_circulation'); }
        if (data.remise_client != null) { setRemiseClient(data.remise_client); highlights.add('remise_client'); }
        if (data.frais && Array.isArray(data.frais)) {
            highlights.add('frais');
            setFrais(prev => {
                const updated = [...prev];
                data.frais.forEach(vf => {
                    const name = (vf.nom || '').toLowerCase();
                    let matched = false;
                    for (let i = 0; i < updated.length; i++) {
                        const fn = updated[i].nom.toLowerCase();
                        if (fn.includes(name) || name.includes(fn) ||
                            (name.includes('carte grise') && fn.includes('circulation')) ||
                            (name.includes('transport') && fn.includes('transport')) ||
                            (name.includes('révision') && fn.includes('révision')) ||
                            (name.includes('pneu') && fn.includes('pneu')) ||
                            (name.includes('garantie') && fn.includes('garantie')) ||
                            (name.includes('contrôle') && fn.includes('contrôle')) ||
                            (name.includes('frein') && fn.includes('frein')) ||
                            (name.includes('batterie') && fn.includes('batterie'))) {
                            updated[i] = { ...updated[i], montant_ttc: vf.montant_ttc || 0 };
                            matched = true;
                            break;
                        }
                    }
                    if (!matched && vf.montant_ttc > 0) {
                        feeIdCounter++;
                        updated.push({ id: `voice-fee-${feeIdCounter}`, nom: vf.nom, montant_ttc: vf.montant_ttc, tva_recuperable: false, icon: 'receipt' });
                    }
                });
                return updated;
            });
        }
        setVoiceHighlights(highlights);
        // Clear highlights after 4 seconds
        setTimeout(() => setVoiceHighlights(new Set()), 4000);
    }, []);

    // Save vehicle
    const saveVehicle = useCallback(async () => {
        const effectiveNom = nom.trim() || modele.trim();
        if (!effectiveNom) {
            toast.error('Veuillez renseigner le véhicule');
            return;
        }
        // Block if active duplicate detected
        if (!id && dupeCheck?.results?.some(r => r.is_actif)) {
            toast.error('Un dossier actif existe déjà pour cette immatriculation. Ouvrez le dossier existant.');
            return;
        }
        // Block if historical duplicate without force confirmation
        if (!id && dupeCheck?.results?.length > 0 && !dupeCheck.results.some(r => r.is_actif) && !dupeForce) {
            toast.error('Confirmez la création en cochant la case de validation.');
            return;
        }
        
        setSaving(true);
        const vehicleData = {
            nom: effectiveNom,
            modele,
            immatriculation,
            prix_achat: prixAchat,
            prix_vente: prixVente,
            frais_circulation: fraisCirculation,
            remise_client: remiseClient,
            date_vente: dateVente || null,
            statut: dateVente ? 'vendu' : statut,
            commercial_id: commercialId || null,
            skip_marge_min_agence: skipMargeMin,
            regime_tva: regimeTva || null,
            force_duplicate: dupeForce,
            frais: frais.map(f => {
                const entry = { nom: f.nom, montant_ttc: f.montant_ttc, tva_recuperable: f.tva_recuperable };
                if (f._fv_id) entry._fv_id = f._fv_id;
                if (f.taux_tva != null) entry.taux_tva = f.taux_tva;
                return entry;
            })
        };

        try {
            if (id) {
                await axios.put(`${API_URL}/api/vehicles/${id}`, vehicleData, {
                    withCredentials: true
                });
                toast.success('Dossier mis à jour');
                setCommissionKey(k => k + 1);
            } else {
                const { data } = await axios.post(`${API_URL}/api/vehicles`, vehicleData, {
                    withCredentials: true
                });
                if (data.warning === 'duplicate_history') {
                    // Show historical vehicles and ask for confirmation
                    setDupeCheck({ results: data.existing.map(e => ({ ...e, is_actif: false })), norm: '' });
                    toast.warning(data.message);
                    setSaving(false);
                    return;
                }
                toast.success('Dossier créé');
                navigate(`/calculator/${data.id}`, { replace: true });
            }
        } catch (error) {
            const detail = error?.response?.data?.detail;
            if (error?.response?.status === 409 && detail?.type === 'actif') {
                setDupeCheck({ results: detail.existing.map(e => ({ ...e, is_actif: true })), norm: '' });
                toast.error(detail.message);
            } else {
                toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la sauvegarde');
            }
        } finally {
            setSaving(false);
        }
    }, [nom, modele, immatriculation, prixAchat, prixVente, fraisCirculation, remiseClient, dateVente, statut, commercialId, skipMargeMin, regimeTva, dupeForce, dupeCheck, frais, id, navigate]);

    // Duplicate
    const duplicateVehicle = useCallback(async () => {
        if (!id) {
            toast.error('Sauvegardez d\'abord le dossier');
            return;
        }
        try {
            const { data } = await axios.post(`${API_URL}/api/vehicles/${id}/duplicate`, {}, {
                withCredentials: true
            });
            toast.success('Dossier dupliqué');
            navigate(`/calculator/${data.id}`);
        } catch (error) {
            console.error('Error duplicating vehicle:', error);
            toast.error('Erreur lors de la duplication');
        }
    }, [id, navigate]);

    // Export PDF
    const exportPDF = useCallback(() => {
        setShowPDF(true);
        setTimeout(() => {
            window.print();
        }, 300);
    }, []);

    // Close PDF view after print
    useEffect(() => {
        const handleAfterPrint = () => {
            setShowPDF(false);
        };
        window.addEventListener('afterprint', handleAfterPrint);
        return () => window.removeEventListener('afterprint', handleAfterPrint);
    }, []);

    // Format price
    const formatPrice = useCallback((price) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(price);
    }, []);

    // Memoize vehicle data for PDF to avoid inline object
    const vehicleDataForPDF = useMemo(() => ({
        id,
        nom,
        modele,
        immatriculation,
        prixAchat,
        prixVente,
        fraisCirculation,
        remiseClient
    }), [id, nom, modele, immatriculation, prixAchat, prixVente, fraisCirculation, remiseClient]);

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#6366F1]" />
            </div>
        );
    }

    // PDF View for printing
    if (showPDF) {
        return (
            <>
                <style>{`
                    @media print {
                        body { 
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                            background: white !important;
                        }
                        .no-print { display: none !important; }
                        .pdf-container { 
                            padding: 20px !important;
                            margin: 0 !important;
                            max-width: 100% !important;
                        }
                    }
                `}</style>
                <div className="no-print fixed top-4 right-4 z-50 flex gap-2">
                    <Button
                        onClick={() => window.print()}
                        className="bg-[#6366F1] hover:bg-[#4F46E5] text-gray-900"
                    >
                        <Printer className="w-4 h-4 mr-2" />
                        Imprimer
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setShowPDF(false)}
                        className="bg-white text-gray-800 border-gray-300 hover:bg-gray-100"
                    >
                        Fermer
                    </Button>
                </div>
                <VehiclePDF
                    ref={pdfRef}
                    vehicle={vehicleDataForPDF}
                    calculations={calculations}
                />
            </>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            {/* Header */}
            <header className={`border-b border-gray-200 sticky top-0 z-50 ${isMobile ? 'bg-white' : 'bg-[#141415]/80 backdrop-blur-xl'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                            <button
                                onClick={() => navigate(isMobile ? '/mes-dossiers' : '/dashboard')}
                                className={`p-2 rounded-lg transition-colors shrink-0 ${isMobile ? 'hover:bg-gray-100' : 'hover:bg-white/10'}`}
                                data-testid="back-btn"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            {isMobile ? (
                                <div className="min-w-0">
                                    <Input
                                        type="text"
                                        value={nom}
                                        onChange={(e) => setNom(e.target.value)}
                                        className={`bg-transparent border-0 p-0 text-base font-semibold focus:ring-0 h-auto ${voiceHighlights.has('nom') ? 'text-emerald-600' : ''}`}
                                        placeholder="Nom du véhicule"
                                        data-testid="vehicle-name-input"
                                    />
                                    <p className="text-xs text-gray-400">{id ? 'Modifier le dossier' : 'Nouveau dossier'}</p>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/20 flex items-center justify-center">
                                        <CalcIcon className="w-5 h-5 text-[#6366F1]" />
                                    </div>
                                    <div>
                                        <Input
                                            type="text"
                                            value={nom}
                                            onChange={(e) => setNom(e.target.value)}
                                            className="bg-transparent border-0 p-0 text-lg font-semibold focus:ring-0 h-auto"
                                            placeholder="Nom du véhicule"
                                            data-testid="vehicle-name-input"
                                        />
                                        <p className="text-xs text-gray-400">{id ? 'Modifier le dossier' : 'Nouveau dossier'}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                            {!isMobile && (
                                <Button variant="ghost" size="sm" onClick={resetForm} className="text-gray-500 hover:text-gray-900" data-testid="reset-btn">
                                    <RotateCcw className="w-4 h-4 mr-2" /><span className="hidden sm:inline">Réinitialiser</span>
                                </Button>
                            )}
                            {!isMobile && id && (
                                <>
                                    <Button variant="ghost" size="sm" onClick={duplicateVehicle} className="text-gray-500 hover:text-gray-900" data-testid="duplicate-btn">
                                        <Copy className="w-4 h-4 mr-2" /><span className="hidden sm:inline">Dupliquer</span>
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={exportPDF} className="text-gray-500 hover:text-gray-900" data-testid="export-pdf-btn">
                                        <FileText className="w-4 h-4 mr-2" /><span className="hidden sm:inline">Exporter PDF</span>
                                    </Button>
                                </>
                            )}
                            <Button
                                onClick={saveVehicle}
                                disabled={saving}
                                className={`${isMobile ? 'bg-[#6366F1] hover:bg-[#4F46E5] text-white h-10 px-4 rounded-xl' : 'bg-[#6366F1] hover:bg-[#4F46E5] text-gray-900'}`}
                                data-testid="save-btn"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                                {isMobile ? 'Enregistrer' : 'Sauvegarder'}
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main content */}
            <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 ${isMobile ? 'pb-8' : ''}`}>
                {/* Voice input for mobile commercial (new vehicle only) */}
                {isMobile && !id && (
                    <div className="mb-5">
                        <VoiceInput onResult={handleVoiceResult} />
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
                    {/* Left column - Inputs */}
                    <div className="lg:col-span-8 space-y-6">
                        {/* Vehicle Info card */}
                        <div className="card-base p-6 animate-fade-in">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-[#6366F1]/10 flex items-center justify-center">
                                    <CarFront className="w-5 h-5 text-[#818CF8]" />
                                </div>
                                <div>
                                    <h2 className="font-semibold text-lg font-['Inter']">Informations véhicule</h2>
                                    <p className="text-xs text-gray-400">Modèle et immatriculation</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-gray-400 uppercase tracking-wider">Véhicule</Label>
                                    <Input
                                        type="text"
                                        value={modele}
                                        onChange={(e) => { setModele(e.target.value); if (!nom.trim()) setNom(e.target.value); }}
                                        className={`bg-white border-gray-200 ${hl(voiceHighlights, 'modele')}`}
                                        placeholder="Ex: Peugeot 308 GT Line"
                                        data-testid="modele-input"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs text-gray-400 uppercase tracking-wider">Plaque d'immatriculation</Label>
                                    <Input
                                        type="text"
                                        value={immatriculation}
                                        onChange={(e) => { const v = e.target.value.toUpperCase(); setImmatriculation(v); checkImmat(v); }}
                                        className={`bg-white border-gray-200 uppercase tracking-wider ${hl(voiceHighlights, 'immatriculation')} ${dupeCheck?.results?.some(r => r.is_actif) ? 'border-red-400 ring-1 ring-red-200' : ''}`}
                                        placeholder="Ex: AB-123-CD"
                                        data-testid="immatriculation-input"
                                    />
                                    {/* Anti-doublon warning */}
                                    {dupeCheck && dupeCheck.results.length > 0 && (
                                        <div className={`rounded-lg border px-3 py-2.5 ${dupeCheck.results.some(r => r.is_actif) ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`} data-testid="dupe-warning">
                                            <p className={`text-[12px] font-semibold mb-1.5 ${dupeCheck.results.some(r => r.is_actif) ? 'text-red-800' : 'text-amber-800'}`}>
                                                {dupeCheck.results.some(r => r.is_actif) ? 'Ce véhicule existe déjà (dossier actif)' : 'Dossier(s) historique(s) trouvé(s)'}
                                            </p>
                                            {dupeCheck.results.map((r, i) => (
                                                <div key={i} className="flex items-center justify-between gap-2 py-1 border-t border-gray-200/50 first:border-0">
                                                    <div className="min-w-0">
                                                        <span className="text-[11px] font-medium text-gray-700">{r.nom}</span>
                                                        <span className="text-[10px] text-gray-500 ml-1 font-mono">{r.immatriculation}</span>
                                                        <span className="text-[10px] text-gray-400 ml-1">— {r.commercial}</span>
                                                        {r.date_vente && <span className="text-[10px] text-gray-400 ml-1">vendu {r.date_vente}</span>}
                                                        <span className={`ml-1 text-[9px] px-1.5 py-0.5 rounded-full font-medium ${r.is_actif ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>{r.is_actif ? 'Actif' : 'Vendu'}</span>
                                                    </div>
                                                    <button onClick={() => navigate(`/calculator/${r.id}`)} className="text-[10px] text-[#6366F1] hover:underline whitespace-nowrap">Ouvrir le dossier</button>
                                                </div>
                                            ))}
                                            {!dupeCheck.results.some(r => r.is_actif) && (
                                                <label className="flex items-center gap-2 mt-2 pt-2 border-t border-amber-200">
                                                    <input type="checkbox" checked={dupeForce} onChange={e => setDupeForce(e.target.checked)} className="rounded border-gray-300" data-testid="dupe-force-checkbox" />
                                                    <span className="text-[11px] text-amber-700">Je confirme la création d'un nouveau dossier pour cette immatriculation</span>
                                                </label>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-gray-400 uppercase tracking-wider">Date de vente</Label>
                                    <DatePickerField
                                        value={dateVente}
                                        onChange={(v) => { setDateVente(v); setStatut(v ? 'vendu' : 'en_stock'); }}
                                        className="border-gray-200"
                                        testId="date-vente-input"
                                    />
                                    <p className="text-xs text-gray-400">Requis pour les commissions. Laissez vide si non vendu.</p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-gray-400 uppercase tracking-wider">Statut</Label>
                                    <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border ${dateVente ? 'bg-emerald-50 border-emerald-500/20 text-emerald-600' : 'bg-white border-gray-200 text-gray-500'}`} data-testid="statut-display">
                                        <div className={`w-2 h-2 rounded-full ${dateVente ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
                                        {dateVente ? 'Vendu' : 'En stock'}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-gray-400 uppercase tracking-wider">Commercial affecté</Label>
                                    <Select value={commercialId || 'none'} onValueChange={v => setCommercialId(v === 'none' ? '' : v)}>
                                        <SelectTrigger className="bg-white border-gray-200" data-testid="commercial-select">
                                            <SelectValue placeholder="Sélectionner un commercial..." />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white border-gray-200">
                                            <SelectItem value="none">Non affecté</SelectItem>
                                            {commerciaux.map(c => (
                                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            {isAdmin && (
                                <div className="mt-4 space-y-2">
                                    <Label className="text-xs text-gray-400 uppercase tracking-wider">Régime TVA</Label>
                                    <Select value={regimeTva || 'tva_marge'} onValueChange={v => setRegimeTva(v)}>
                                        <SelectTrigger className="bg-white border-gray-200" data-testid="regime-tva-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white border-gray-200">
                                            <SelectItem value="tva_marge">TVA sur marge</SelectItem>
                                            <SelectItem value="tva_prix_total">TVA sur prix total</SelectItem>
                                            <SelectItem value="exonere">Exonéré de TVA</SelectItem>
                                            <SelectItem value="intracommunautaire">Intracommunautaire</SelectItem>
                                            <SelectItem value="import_hors_ue">Import hors UE</SelectItem>
                                            <SelectItem value="autoliquidation">Autoliquidation</SelectItem>
                                            <SelectItem value="autre">Autre / cas particulier</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-gray-400">Par défaut : TVA sur marge. Modifiez uniquement si nécessaire.</p>
                                </div>
                            )}
                            {isAdmin && (
                                <div className="mt-3 flex items-center gap-2">
                                    <input type="checkbox" id="skip-marge-min" checked={skipMargeMin} onChange={e => setSkipMargeMin(e.target.checked)}
                                        className="rounded border-gray-300 text-[#6366F1] focus:ring-[#6366F1] w-4 h-4" data-testid="skip-marge-min-checkbox" />
                                    <label htmlFor="skip-marge-min" className="text-[12px] text-gray-500 cursor-pointer">Ne pas appliquer le minimum agence</label>
                                </div>
                            )}
                        </div>

                        {/* Transaction card */}
                        <div className="card-base p-6 animate-fade-in stagger-1">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-[#6366F1]/10 flex items-center justify-center">
                                    <Car className="w-5 h-5 text-[#818CF8]" />
                                </div>
                                <div>
                                    <h2 className="font-semibold text-lg font-['Inter']">Transaction</h2>
                                    <p className="text-xs text-gray-400">Prix d'achat, vente et remises</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-gray-400 uppercase tracking-wider">Prix d'achat</Label>
                                    <div className="relative">
                                        <Input
                                            type="number" inputMode="decimal"
                                            value={prixAchat || ''}
                                            onChange={(e) => setPrixAchat(parseFloat(e.target.value) || 0)}
                                            className={`bg-white border-gray-200 pr-8 ${hl(voiceHighlights, 'prix_achat')}`}
                                            placeholder="0"
                                            data-testid="prix-achat-input"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs text-gray-400 uppercase tracking-wider">Prix de vente</Label>
                                    <div className="relative">
                                        <Input
                                            type="number" inputMode="decimal"
                                            value={prixVente || ''}
                                            onChange={(e) => setPrixVente(parseFloat(e.target.value) || 0)}
                                            className={`bg-white border-gray-200 pr-8 ${hl(voiceHighlights, 'prix_vente')}`}
                                            placeholder="0"
                                            data-testid="prix-vente-input"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs text-gray-400 uppercase tracking-wider">Frais de mise en circulation</Label>
                                    <div className="relative">
                                        <Input
                                            type="number" inputMode="decimal"
                                            value={fraisCirculation || ''}
                                            onChange={(e) => setFraisCirculation(parseFloat(e.target.value) || 0)}
                                            className={`bg-white border-gray-200 pr-8 ${hl(voiceHighlights, 'frais_circulation')}`}
                                            placeholder="0"
                                            data-testid="frais-circulation-input"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs text-gray-400 uppercase tracking-wider">Remise client</Label>
                                    <div className="relative">
                                        <Input
                                            type="number" inputMode="decimal"
                                            value={remiseClient || ''}
                                            onChange={(e) => setRemiseClient(parseFloat(e.target.value) || 0)}
                                            className={`bg-white border-gray-200 pr-8 ${hl(voiceHighlights, 'remise_client')}`}
                                            placeholder="0"
                                            data-testid="remise-client-input"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Fees card */}
                        <div className={`card-base p-6 animate-fade-in stagger-2 ${voiceHighlights.has('frais') ? 'ring-2 ring-emerald-400' : ''}`}>
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-amber-600/10 flex items-center justify-center">
                                        <ReceiptText className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <h2 className="font-semibold text-lg font-['Inter']">Frais liés au véhicule</h2>
                                        <p className="text-xs text-gray-400">Cochez "TVA" si la TVA est récupérable</p>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={addCustomFee}
                                    className="text-[#818CF8] hover:text-gray-900"
                                    data-testid="add-fee-btn"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Ajouter
                                </Button>
                            </div>

                            {/* Table header */}
                            <div className="hidden sm:grid grid-cols-12 gap-3 px-2 mb-2">
                                <div className="col-span-4 text-xs text-gray-400 uppercase tracking-wider">Frais</div>
                                <div className="col-span-2 text-xs text-gray-400 uppercase tracking-wider text-right">TTC</div>
                                <div className="col-span-2 text-xs text-gray-400 uppercase tracking-wider text-center">TVA</div>
                                <div className="col-span-2 text-xs text-gray-400 uppercase tracking-wider text-right">HT</div>
                                <div className="col-span-2 text-xs text-gray-400 uppercase tracking-wider text-right">TVA Récup.</div>
                            </div>

                            {/* Fee rows */}
                            <div className="space-y-1">
                                {calculations.fraisDetails.map((fee, index) => (
                                    <div key={fee.id} className="fee-row group" data-testid={`fee-row-${index}`}>
                                        <div className="grid grid-cols-12 gap-3 items-center w-full">
                                            {/* Name */}
                                            <div className="col-span-12 sm:col-span-4 flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-lg bg-[#1F1F22] flex items-center justify-center text-gray-500 shrink-0">
                                                    {getIcon(fee.icon)}
                                                </div>
                                                <Input
                                                    type="text"
                                                    value={fee.nom}
                                                    onChange={(e) => updateFee(index, 'nom', e.target.value)}
                                                    className="bg-transparent border-0 p-0 h-auto text-sm focus:ring-0"
                                                    data-testid={`fee-name-${index}`}
                                                />
                                            </div>
                                            
                                            {/* TTC input */}
                                            <div className="col-span-4 sm:col-span-2">
                                                <div className="relative">
                                                    <Input
                                                        type="number" inputMode="decimal"
                                                        value={fee.montant_ttc || ''}
                                                        onChange={(e) => updateFee(index, 'montant_ttc', parseFloat(e.target.value) || 0)}
                                                        className="bg-white border-gray-200 text-right pr-6 text-sm h-9"
                                                        placeholder="0"
                                                        data-testid={`fee-ttc-${index}`}
                                                    />
                                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">€</span>
                                                </div>
                                            </div>
                                            
                                            {/* TVA checkbox */}
                                            <div className="col-span-2 sm:col-span-2 flex justify-center">
                                                <Checkbox
                                                    checked={fee.tva_recuperable}
                                                    onCheckedChange={(checked) => updateFee(index, 'tva_recuperable', checked)}
                                                    className="data-[state=checked]:bg-[#6366F1] data-[state=checked]:border-[#6366F1]"
                                                    data-testid={`fee-tva-${index}`}
                                                />
                                            </div>
                                            
                                            {/* HT calculated */}
                                            <div className="col-span-3 sm:col-span-2 text-right">
                                                <span className="text-sm font-mono text-gray-500" data-testid={`fee-ht-${index}`}>
                                                    {formatPrice(fee.ht)}
                                                </span>
                                            </div>
                                            
                                            {/* TVA amount */}
                                            <div className="col-span-3 sm:col-span-2 flex items-center justify-end gap-2">
                                                <span className="text-sm font-mono text-emerald-600" data-testid={`fee-tva-amount-${index}`}>
                                                    {fee.tva > 0 ? formatPrice(fee.tva) : '-'}
                                                </span>
                                                {index >= DEFAULT_FEES.length && (
                                                    <button
                                                        onClick={() => removeFee(index)}
                                                        className="p-1 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                                        data-testid={`remove-fee-${index}`}
                                                    >
                                                        <Trash2 className="w-3 h-3 text-red-600" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Totals */}
                            <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-400">Total frais TTC</span>
                                    <span className="font-mono" data-testid="total-frais-ttc">{formatPrice(calculations.totalFraisTTC)}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-400">Total frais HT</span>
                                    <span className="font-mono" data-testid="total-frais-ht">{formatPrice(calculations.totalFraisHT)}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-400">Total TVA déductible</span>
                                    <span className="font-mono text-emerald-600" data-testid="total-tva-deductible">{formatPrice(calculations.totalTVADeductible)}</span>
                                </div>
                            </div>
                        </div>
                        {id && <VehicleDocuments vehicleId={id} />}
                    </div>

                    {/* Right column - Summary */}
                    <div className="lg:col-span-4">
                        <div className="sticky-summary glass card-base p-6 border border-gray-200 animate-fade-in stagger-3">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-emerald-600/10 flex items-center justify-center">
                                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                    <h2 className="font-semibold text-lg font-['Inter']">Résumé</h2>
                                    <p className="text-xs text-gray-400">Calculs automatiques</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {/* Vente réelle TTC */}
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-sm text-gray-500">Vente réelle TTC</span>
                                    <span className="font-mono font-medium" data-testid="vente-reelle-ttc">{formatPrice(calculations.venteReelleTTC)}</span>
                                </div>

                                {/* Marge brute TTC */}
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-sm text-gray-500">Marge brute TTC</span>
                                    <span className="font-mono font-medium" data-testid="marge-brute-ttc">{formatPrice(calculations.margeBruteTTC)}</span>
                                </div>

                                {/* TVA sur marge */}
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-sm text-gray-500">TVA sur marge</span>
                                    <span className="font-mono text-red-600" data-testid="tva-sur-marge">{calculations.tvaSurMarge > 0 ? `- ${formatPrice(calculations.tvaSurMarge)}` : formatPrice(0)}</span>
                                </div>

                                {/* Marge brute HT avant frais */}
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-sm text-gray-500">Marge brute HT avant frais</span>
                                    <span className="font-mono font-medium" data-testid="marge-brute-ht">{formatPrice(calculations.margeBruteHTAvantFrais)}</span>
                                </div>

                                {/* Total frais HT */}
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-sm text-gray-500">Total frais HT</span>
                                    <span className="font-mono text-red-600" data-testid="summary-frais-ht">- {formatPrice(calculations.totalFraisHT)}</span>
                                </div>

                                {/* TVA déductible */}
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-sm text-gray-500">TVA déductible sur frais</span>
                                    <span className="font-mono text-emerald-600" data-testid="summary-tva-deductible">+ {formatPrice(calculations.totalTVADeductible)}</span>
                                </div>

                                {/* TVA nette dossier */}
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-sm text-gray-500">TVA nette dossier</span>
                                    <span className={`font-mono ${calculations.tvaNetteDossier >= 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="tva-nette-dossier">
                                        {calculations.tvaNetteDossier >= 0 ? '- ' : '+ '}{formatPrice(Math.abs(calculations.tvaNetteDossier))}
                                    </span>
                                </div>
                            </div>

                            {/* Final margin card */}
                            <div className={`mt-6 p-4 rounded-xl border ${marginStatus.bg}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-gray-400 uppercase tracking-wider">Marge nette HT réelle</span>
                                    <div className={`flex items-center gap-1 ${marginStatus.color}`}>
                                        <MarginIcon className="w-4 h-4" />
                                        <span className="text-xs font-medium">{marginStatus.label}</span>
                                    </div>
                                </div>
                                <div className={`text-3xl font-bold font-mono tracking-tighter ${marginStatus.color}`} data-testid="marge-nette-ht">
                                    {formatPrice(calculations.margeNetteHT)}
                                </div>
                            </div>
                            {id && <VehicleCommissionBlock vehicleId={id} refreshKey={commissionKey} />}

                            {/* Export PDF button for mobile */}
                            {id && (
                                <Button
                                    onClick={exportPDF}
                                    className="w-full mt-4 bg-[#1F1F22] hover:bg-[#27272A] text-gray-900 border border-gray-200"
                                    data-testid="export-pdf-mobile-btn"
                                >
                                    <FileText className="w-4 h-4 mr-2" />
                                    Exporter en PDF
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
